///
/// EarthBrowser
///
/// Created by Matt Giger on 10/24/2026
/// Copyright (c) 2026 Geodata Labs
///

/**
 * Shadow copy of the WebGL state the render loop touches most, so redundant
 * driver calls are skipped: consecutive tiles share a program, VAO, textures
 * and blend/depth state, and without this each draw re-issued all of them.
 *
 * `undefined` means "unknown": the next call always goes to the driver. The
 * engine invalidates once per frame, so code that drives `gl` directly (e.g.
 * offscreen bakers) only needs to leave the state as it found it; anything
 * that can't should call `invalidate()` afterwards.
 */
export class GLState {
	private readonly gl: WebGL2RenderingContext

	/**
	 * Depth-write default for draws in the current pass (true for opaque,
	 * false for transparent); set by the viewports, read by renderers whose
	 * DrawState leaves depthWrite unset
	 */
	public passDepthWrite: boolean = true

	private program: WebGLProgram | null | undefined
	private vertexArray: WebGLVertexArrayObject | null | undefined
	private activeUnit: number | undefined
	private textures2D: (WebGLTexture | null | undefined)[] = []
	private texturesCube: (WebGLTexture | null | undefined)[] = []
	private capabilities = new Map<GLenum, boolean>()
	private depthWrite: boolean | undefined
	private blend: [GLenum, GLenum, GLenum, GLenum] | undefined
	private offset: [number, number] | undefined
	private viewportRect: [number, number, number, number] | undefined
	private pixelStore = new Map<GLenum, number | boolean>()

	constructor(gl: WebGL2RenderingContext) {
		this.gl = gl
	}

	/** Forget everything; the next call of each kind reaches the driver */
	public invalidate(): void {
		this.program = undefined
		this.vertexArray = undefined
		this.activeUnit = undefined
		this.textures2D.length = 0
		this.texturesCube.length = 0
		this.capabilities.clear()
		this.depthWrite = undefined
		this.blend = undefined
		this.offset = undefined
		this.viewportRect = undefined
		this.pixelStore.clear()
	}

	/** Pixel-store (unpack) parameter, issued only when it changes */
	public pixelStorei(pname: GLenum, param: number | boolean): void {
		if(this.pixelStore.get(pname) !== param) {
			this.gl.pixelStorei(pname, param as number)
			this.pixelStore.set(pname, param)
		}
	}

	public viewport(x: number, y: number, width: number, height: number): void {
		const current = this.viewportRect
		if(current === undefined || current[0] !== x || current[1] !== y || current[2] !== width || current[3] !== height) {
			this.gl.viewport(x, y, width, height)
			this.viewportRect = [x, y, width, height]
		}
	}

	/**
	 * Current viewport rect; queries the driver only while unknown. Lets
	 * offscreen passes save and restore it without a getParameter round trip
	 */
	public getViewport(): [number, number, number, number] {
		if(this.viewportRect === undefined) {
			const v = this.gl.getParameter(WebGL2RenderingContext.VIEWPORT) as Int32Array
			this.viewportRect = [v[0], v[1], v[2], v[3]]
		}
		return this.viewportRect
	}

	/** Whether a capability is enabled; queries the driver only while unknown */
	public isEnabled(capability: GLenum): boolean {
		let enabled = this.capabilities.get(capability)
		if(enabled === undefined) {
			enabled = this.gl.isEnabled(capability)
			this.capabilities.set(capability, enabled)
		}
		return enabled
	}

	public useProgram(program: WebGLProgram | null): void {
		if(this.program !== program) {
			this.gl.useProgram(program)
			this.program = program
		}
	}

	public bindVertexArray(vertexArray: WebGLVertexArrayObject | null): void {
		if(this.vertexArray !== vertexArray) {
			this.gl.bindVertexArray(vertexArray)
			this.vertexArray = vertexArray
		}
	}

	public activeTexture(unit: number): void {
		if(this.activeUnit !== unit) {
			this.gl.activeTexture(WebGL2RenderingContext.TEXTURE0 + unit)
			this.activeUnit = unit
		}
	}

	/**
	 * Bind a texture on the active unit. Texture uploads use this so the
	 * cache stays in step with the bindings they disturb mid-frame.
	 */
	public bindTexture(target: GLenum, texture: WebGLTexture | null): void {
		if(this.activeUnit === undefined) {
			// Can't record a binding without knowing the unit it lands on
			this.activeTexture(0)
		}
		const bound = target === WebGL2RenderingContext.TEXTURE_CUBE_MAP ? this.texturesCube : this.textures2D
		const unit = this.activeUnit as number
		if(bound[unit] !== texture) {
			this.gl.bindTexture(target, texture)
			bound[unit] = texture
		}
	}

	public bindTextureUnit(unit: number, target: GLenum, texture: WebGLTexture | null): void {
		const bound = target === WebGL2RenderingContext.TEXTURE_CUBE_MAP ? this.texturesCube : this.textures2D
		if(bound[unit] !== texture) {
			this.activeTexture(unit)
			this.gl.bindTexture(target, texture)
			bound[unit] = texture
		}
	}

	/** A texture is being deleted: drop any unit that still references it */
	public forgetTexture(texture: WebGLTexture): void {
		for(let i = 0; i < this.textures2D.length; i++) {
			if(this.textures2D[i] === texture) this.textures2D[i] = undefined
		}
		for(let i = 0; i < this.texturesCube.length; i++) {
			if(this.texturesCube[i] === texture) this.texturesCube[i] = undefined
		}
	}

	public setEnabled(capability: GLenum, enabled: boolean): void {
		if(this.capabilities.get(capability) !== enabled) {
			if(enabled) {
				this.gl.enable(capability)
			} else {
				this.gl.disable(capability)
			}
			this.capabilities.set(capability, enabled)
		}
	}

	public enable(capability: GLenum): void {
		this.setEnabled(capability, true)
	}

	public disable(capability: GLenum): void {
		this.setEnabled(capability, false)
	}

	public depthMask(enabled: boolean): void {
		if(this.depthWrite !== enabled) {
			this.gl.depthMask(enabled)
			this.depthWrite = enabled
		}
	}

	/** Current depth write mask; queries the driver only while unknown */
	public get depthWriteMask(): boolean {
		this.depthWrite ??= this.gl.getParameter(WebGL2RenderingContext.DEPTH_WRITEMASK) as boolean
		return this.depthWrite
	}

	/** Depth bias applied while POLYGON_OFFSET_FILL is enabled */
	public polygonOffset(factor: number, units: number): void {
		const current = this.offset
		if(current === undefined || current[0] !== factor || current[1] !== units) {
			this.gl.polygonOffset(factor, units)
			this.offset = [factor, units]
		}
	}

	public blendFunc(sfactor: GLenum, dfactor: GLenum): void {
		this.blendFuncSeparate(sfactor, dfactor, sfactor, dfactor)
	}

	public blendFuncSeparate(srcRGB: GLenum, dstRGB: GLenum, srcAlpha: GLenum, dstAlpha: GLenum): void {
		const current = this.blend
		if(current === undefined || current[0] !== srcRGB || current[1] !== dstRGB || current[2] !== srcAlpha || current[3] !== dstAlpha) {
			this.gl.blendFuncSeparate(srcRGB, dstRGB, srcAlpha, dstAlpha)
			this.blend = [srcRGB, dstRGB, srcAlpha, dstAlpha]
		}
	}
}
